import React from "react";
import ReactApexChart from "react-apexcharts";
import { getNiceMax } from "../utils/chartDataUtils";

const MIN_BAR_WIDTH = 50;
const COLOR = "#16a34a";

// SG-2164: Call Outcomes is a categorical breakdown (one bar per outcome). Its inherent default is
// BAR (see getDefaultVisualization), so a null/undefined visualization renders as bar. Users can
// switch to a Line chart (x-axis hidden, outcome shown on hover — long outcome names never fit under
// ticks at small widths) or a KPI numeric summary.
const CallOutcomesWidget = ({ callOutcomes = [], visualization }) => {
  if (!callOutcomes.length) {
    return <p className="callOutcomesEmpty">No call outcomes data</p>;
  }

  const labels = callOutcomes.map(o => o.outcome);
  const counts = callOutcomes.map(o => o.count);
  const { max: niceMax, tickAmount } = getNiceMax(Math.max(...counts, 1));

  const isKpi = visualization === "KPI";
  const isLine = visualization === "LINE";
  const isBar = !isLine && !isKpi; // null/undefined => bar (this widget's default)

  if (isKpi) {
    return (
      <div className="widgetCardNumericSummary">
        {callOutcomes.map(o => (
          <div key={o.outcome} className="widgetCardNumericSummaryRow">
            <span className="widgetCardNumericSummaryLabel">{o.outcome}</span>
            <span className="widgetCardNumericSummaryValue">{o.count}</span>
          </div>
        ))}
      </div>
    );
  }

  const options = {
    chart: {
      // "area" (not "line") so the connecting line renders reliably — matches RateWidget/CountWidget.
      type: isLine ? "area" : "bar",
      toolbar: { show: false },
      animations: { enabled: false },
      selection: { enabled: false },
      zoom: { enabled: false }
    },
    ...(isLine
      ? {
          stroke: { show: true, curve: "smooth", width: 2, colors: [COLOR] },
          markers: { size: 4, colors: [COLOR], strokeColors: "#fff", strokeWidth: 2, hover: { size: 5 } },
          fill: { type: "solid", opacity: 0.2 }
        }
      : {
          plotOptions: { bar: { borderRadius: 3, columnWidth: "90%", distributed: true } }
        }),
    colors: [COLOR],
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: {
      categories: labels,
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false }
    },
    yaxis: {
      min: 0,
      max: niceMax,
      tickAmount,
      labels: {
        formatter: val => Math.round(val),
        style: { fontSize: "10px", colors: "#999" }
      }
    },
    grid: {
      borderColor: "#e8ebee",
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } }
    },
    tooltip: {
      x: { show: true },
      y: { formatter: val => `${val} calls` }
    }
  };

  const series = [{ name: "Calls", data: counts }];

  // Line fits any width; bar needs a min-width so narrow columns stay legible and scroll.
  const chart = (
    // key forces a fresh chart instance on type change — react-apexcharts leaves a chart half-drawn
    // (markers only, no line) when chart.type is swapped in place on the same mounted instance.
    <ReactApexChart
      key={isLine ? "area" : "bar"}
      options={options}
      series={series}
      type={isLine ? "area" : "bar"}
      height={140}
      width="100%"
    />
  );

  return (
    <div className="callOutcomesChartWrap">
      {isBar ? (
        <div className="callOutcomesChartScroll" style={{ minWidth: `${labels.length * MIN_BAR_WIDTH}px` }}>
          {chart}
        </div>
      ) : (
        chart
      )}
    </div>
  );
};

export default CallOutcomesWidget;
